import { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/components/Header";

type ConversaApiItem = {
  conversa_id: number;
  user_id: number;
  nome: string;
  avatar?: string | null;
  ultima_mensagem?: string | null;
  ultima_data?: string | null;
  reserva_id?: number | null;
  servico_id?: number | null;
};

type MensagemItem = {
  id: number;
  mensagem: string;
  remetente_id: number;
  created_at?: string | null;
};

type ConversaResponse = {
  conversa_id?: number | null;
  meu_id?: number | string;
  mensagens?: MensagemItem[];
};

type MensagemUI = {
  id: number;
  texto: string;
  autor: "eu" | "outro";
  created_at?: string;
};

type GrupoMensagens = {
  chave: string;
  rotulo: string;
  mensagens: MensagemUI[];
};

function getCsrfToken(): string {
  const el = document.querySelector(
    'meta[name="csrf-token"]'
  ) as HTMLMetaElement | null;

  return el?.content ?? "";
}

function normalizarMensagem(payload: any): MensagemItem | null {
  const msg =
    payload && typeof payload === "object" && "id" in payload
      ? payload
      : payload?.mensagem && typeof payload.mensagem === "object"
      ? payload.mensagem
      : null;

  if (!msg?.id || !msg?.mensagem) {
    return null;
  }

  return {
    id: Number(msg.id),
    mensagem: String(msg.mensagem),
    remetente_id: Number(msg.remetente_id),
    created_at: msg.created_at ?? null,
  };
}

function normalizarDataMensagem(msg: MensagemUI): Date {
  const valor = msg.created_at || "";

  if (valor) {
    const dt = new Date(valor);
    if (!Number.isNaN(dt.getTime())) return dt;
  }

  return new Date();
}

function formatarHoraMensagem(msg: MensagemUI): string {
  const dt = normalizarDataMensagem(msg);

  return dt.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatarRotuloData(data: Date): string {
  const hoje = new Date();
  const ontem = new Date();
  ontem.setDate(hoje.getDate() - 1);

  const dataBase = new Date(data);
  dataBase.setHours(0, 0, 0, 0);

  const hojeBase = new Date(hoje);
  hojeBase.setHours(0, 0, 0, 0);

  const ontemBase = new Date(ontem);
  ontemBase.setHours(0, 0, 0, 0);

  if (dataBase.getTime() === hojeBase.getTime()) return "Hoje";
  if (dataBase.getTime() === ontemBase.getTime()) return "Ontem";

  return data.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function agruparMensagensPorData(mensagens: MensagemUI[]): GrupoMensagens[] {
  const grupos = new Map<string, GrupoMensagens>();

  mensagens.forEach((msg) => {
    const data = normalizarDataMensagem(msg);

    const chave = [
      data.getFullYear(),
      String(data.getMonth() + 1).padStart(2, "0"),
      String(data.getDate()).padStart(2, "0"),
    ].join("-");

    if (!grupos.has(chave)) {
      grupos.set(chave, {
        chave,
        rotulo: formatarRotuloData(data),
        mensagens: [],
      });
    }

    grupos.get(chave)!.mensagens.push(msg);
  });

  return Array.from(grupos.values()).sort((a, b) =>
    a.chave.localeCompare(b.chave)
  );
}

function getAvatarUrl(path?: string | null) {
  if (!path) return null;

  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  if (path.startsWith("/storage/")) {
    return path;
  }

  if (path.startsWith("/")) {
    return `/storage${path}`;
  }

  return `/storage/${path}`;
}

function avatarTexto(nome: string) {
  return nome
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte.charAt(0).toUpperCase())
    .join("");
}

function formatarHorarioLista(valor?: string | null) {
  if (!valor) return "";

  try {
    const data = new Date(valor);

    if (Number.isNaN(data.getTime())) {
      return "";
    }

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(data);
  } catch {
    return "";
  }
}

export default function InboxPage() {
  const [busca, setBusca] = useState("");
  const [carregandoConversas, setCarregandoConversas] = useState(true);
  const [carregandoMensagens, setCarregandoMensagens] = useState(false);
  const [erro, setErro] = useState("");
  const [conversas, setConversas] = useState<ConversaApiItem[]>([]);
  const [conversaSelecionada, setConversaSelecionada] =
    useState<ConversaApiItem | null>(null);
  const [mensagens, setMensagens] = useState<MensagemItem[]>([]);
  const [novaMensagem, setNovaMensagem] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [meuId, setMeuId] = useState<number>(0);

  const mensagensRef = useRef<HTMLDivElement | null>(null);

  const mensagensUI = useMemo<MensagemUI[]>(() => {
    return mensagens.map((msg) => ({
      id: msg.id,
      texto: msg.mensagem,
      autor: Number(msg.remetente_id) === Number(meuId) ? "eu" : "outro",
      created_at: msg.created_at ?? undefined,
    }));
  }, [mensagens, meuId]);

  const mensagensAgrupadas = useMemo(() => {
    return agruparMensagensPorData(mensagensUI);
  }, [mensagensUI]);

  const conversasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    if (!termo) return conversas;

    return conversas.filter((conversa) => {
      const nome = String(conversa.nome || "").toLowerCase();
      const ultimaMensagem = String(
        conversa.ultima_mensagem || ""
      ).toLowerCase();

      return nome.includes(termo) || ultimaMensagem.includes(termo);
    });
  }, [busca, conversas]);

  useEffect(() => {
    carregarConversas();
  }, []);

  useEffect(() => {
    if (!mensagensRef.current) return;

    mensagensRef.current.scrollTo({
      top: mensagensRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [mensagens]);

  useEffect(() => {
    if (!conversaSelecionada?.conversa_id) return;

    const echo = (window as any).Echo;
    if (!echo) return;

    const channelName = `chat.${conversaSelecionada.conversa_id}`;

    try {
      echo.leave(channelName);
    } catch {}

    const channel = echo.private(channelName);

    channel.listen(".NovaMensagemEnviada", (event: any) => {
      const msg = normalizarMensagem(event);
      if (!msg) return;

      setMensagens((prev) => {
        const existe = prev.some((item) => item.id === msg.id);
        if (existe) return prev;
        return [...prev, msg];
      });

      setConversas((prev) =>
        prev.map((conversa) =>
          conversa.conversa_id === conversaSelecionada.conversa_id
            ? {
                ...conversa,
                ultima_mensagem: msg.mensagem,
                ultima_data: msg.created_at ?? new Date().toISOString(),
              }
            : conversa
        )
      );
    });

    return () => {
      try {
        echo.leave(channelName);
      } catch {}
    };
  }, [conversaSelecionada?.conversa_id]);

  async function carregarConversas() {
    try {
      setCarregandoConversas(true);
      setErro("");

      const resp = await fetch("/chat/conversas", {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!resp.ok) {
        throw new Error("Erro ao carregar conversas.");
      }

      const data = await resp.json();
      const lista = Array.isArray(data) ? data : [];

      setConversas(lista);

      if (lista.length > 0) {
        await selecionarConversa(lista[0], false);
      } else {
        setConversaSelecionada(null);
        setMensagens([]);
      }
    } catch (error) {
      console.error(error);
      setErro("Não foi possível carregar as conversas.");
    } finally {
      setCarregandoConversas(false);
    }
  }

  async function selecionarConversa(
    conversa: ConversaApiItem,
    atualizarLista = false
  ) {
    try {
      setConversaSelecionada(conversa);
      setCarregandoMensagens(true);
      setErro("");

      const resp = await fetch(`/chat/get-conversa/${conversa.user_id}`, {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!resp.ok) {
        throw new Error("Erro ao carregar mensagens.");
      }

      const data: ConversaResponse = await resp.json();

      setMeuId(Number(data.meu_id ?? 0));
      setMensagens(Array.isArray(data.mensagens) ? data.mensagens : []);

      if (typeof data.conversa_id === "number") {
        setConversaSelecionada((prev) =>
          prev
            ? {
                ...prev,
                conversa_id: data.conversa_id ?? prev.conversa_id,
              }
            : conversa
        );
      }

      if (atualizarLista) {
        await recarregarConversasSilencioso();
      }
    } catch (error) {
      console.error(error);
      setErro("Não foi possível carregar as mensagens.");
      setMensagens([]);
    } finally {
      setCarregandoMensagens(false);
    }
  }

  async function recarregarConversasSilencioso() {
    try {
      const resp = await fetch("/chat/conversas", {
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!resp.ok) return;

      const data = await resp.json();
      const lista = Array.isArray(data) ? data : [];

      setConversas(lista);
    } catch {}
  }

  async function enviarMensagem() {
    if (!conversaSelecionada) return;

    const texto = novaMensagem.trim();
    if (!texto || enviando) return;

    try {
      setEnviando(true);
      setErro("");

      const conversaId = Number(conversaSelecionada.conversa_id ?? 0);

      if (!conversaId) {
        throw new Error("Conversa não encontrada.");
      }

      const resp = await fetch("/chat/enviar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-TOKEN": getCsrfToken(),
        },
        credentials: "same-origin",
        body: JSON.stringify({
          conversa_id: conversaId,
          mensagem: texto,
        }),
      });

      if (!resp.ok) {
        throw new Error("Erro ao enviar mensagem.");
      }

      const data = await resp.json();
      const msg = normalizarMensagem(data);

      if (msg) {
        setMensagens((prev) => {
          const existe = prev.some((item) => item.id === msg.id);
          if (existe) return prev;
          return [...prev, msg];
        });

        setConversas((prev) =>
          prev.map((conversa) =>
            conversa.conversa_id === conversaSelecionada.conversa_id
              ? {
                  ...conversa,
                  ultima_mensagem: msg.mensagem,
                  ultima_data: msg.created_at ?? new Date().toISOString(),
                }
              : conversa
          )
        );
      }

      setNovaMensagem("");
      await recarregarConversasSilencioso();
    } catch (error) {
      console.error(error);
      setErro("Não foi possível enviar a mensagem.");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#f7f7f8]">
      <Header />

      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="grid h-[82vh] min-h-0 grid-cols-1 md:grid-cols-[340px_1fr]">
            <aside className="min-h-0 border-r border-gray-200 bg-white">
              <div className="border-b border-gray-200 px-4 py-4">
                <h1 className="text-lg font-semibold text-gray-900">
                  Conversas
                </h1>
                <p className="text-sm text-gray-500">
                  Seu chat em tempo real
                </p>
              </div>

              <div className="flex h-[calc(82vh-73px)] min-h-0 flex-col p-3">
                <div className="mb-3">
                  <input
                    type="text"
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar conversa..."
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm outline-none focus:border-[#FE5668] focus:bg-white"
                    autoComplete="off"
                  />
                </div>

                {erro && (
                  <div className="mb-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-3 text-sm text-red-700">
                    {erro}
                  </div>
                )}

                <div className="min-h-0 flex-1 space-y-2 overflow-y-auto">
                  {carregandoConversas && (
                    <div className="rounded-2xl border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                      Carregando conversas...
                    </div>
                  )}

                  {!carregandoConversas && conversasFiltradas.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-gray-200 px-3 py-6 text-center text-sm text-gray-500">
                      Nenhuma conversa encontrada.
                    </div>
                  )}

                  {!carregandoConversas &&
                    conversasFiltradas.map((conversa) => {
                      const avatarUrl = getAvatarUrl(conversa.avatar);
                      const ativa =
                        conversaSelecionada?.conversa_id === conversa.conversa_id;

                      return (
                        <button
                          key={conversa.conversa_id}
                          type="button"
                          onClick={() => selecionarConversa(conversa)}
                          className={`w-full rounded-2xl px-3 py-3 text-left transition ${
                            ativa
                              ? "border border-[#FE5668]/20 bg-[#fff4f5]"
                              : "border border-transparent hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#FE5668] text-sm font-bold text-white">
                              {avatarUrl ? (
                                <img
                                  src={avatarUrl}
                                  alt={conversa.nome || "Usuário"}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                avatarTexto(conversa.nome || "U")
                              )}
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <h2 className="truncate text-sm font-semibold text-gray-900">
                                  {conversa.nome || "Usuário"}
                                </h2>

                                <span className="text-xs text-gray-400">
                                  {formatarHorarioLista(conversa.ultima_data)}
                                </span>
                              </div>

                              <p className="mt-1 truncate text-sm text-gray-500">
                                {conversa.ultima_mensagem ||
                                  "Sem mensagens ainda."}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            </aside>

            <section className="flex min-h-0 flex-col bg-[#efeae2]">
              <div className="border-b border-gray-200 bg-white px-4 py-4">
                {conversaSelecionada ? (
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-full bg-[#FE5668] text-sm font-bold text-white">
                      {getAvatarUrl(conversaSelecionada.avatar) ? (
                        <img
                          src={getAvatarUrl(conversaSelecionada.avatar) || ""}
                          alt={conversaSelecionada.nome || "Usuário"}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        avatarTexto(conversaSelecionada.nome || "U")
                      )}
                    </div>

                    <div>
                      <div className="text-sm font-semibold text-gray-900">
                        {conversaSelecionada.nome || "Usuário"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Conversa ativa
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm font-semibold text-gray-900">
                      Selecione uma conversa
                    </div>
                    <div className="text-xs text-gray-500">
                      Escolha um contato na lateral
                    </div>
                  </>
                )}
              </div>

              <div
                ref={mensagensRef}
                className="min-h-0 flex-1 overflow-y-auto p-4"
              >
                {!conversaSelecionada && (
                  <div className="flex h-full items-center justify-center rounded-2xl border border-dashed border-gray-300 bg-white/60 text-sm text-gray-500">
                    Selecione uma conversa para começar
                  </div>
                )}

                {conversaSelecionada && carregandoMensagens && (
                  <div className="text-sm text-gray-500">
                    Carregando mensagens...
                  </div>
                )}

                {conversaSelecionada &&
                  !carregandoMensagens &&
                  mensagens.length === 0 && (
                    <div className="text-sm text-gray-500">
                      Nenhuma mensagem nesta conversa ainda.
                    </div>
                  )}

                {conversaSelecionada &&
                  !carregandoMensagens &&
                  mensagens.length > 0 && (
                    <div className="space-y-3">
                      {mensagensAgrupadas.map((grupo) => (
                        <div key={grupo.chave} className="space-y-3">
                          <div className="sticky top-2 z-10 flex justify-center">
                            <span className="rounded-full border border-gray-200 bg-white/90 px-3 py-1 text-xs font-medium text-gray-500 shadow-sm backdrop-blur">
                              {grupo.rotulo}
                            </span>
                          </div>

                          {grupo.mensagens.map((mensagem) => {
                            const ehMinha = mensagem.autor === "eu";

                            return (
                              <div
                                key={mensagem.id}
                                className={`flex ${
                                  ehMinha ? "justify-end" : "justify-start"
                                }`}
                              >
                                <div
                                  className={[
                                    "max-w-[82%] rounded-2xl px-4 py-2 shadow-sm",
                                    "sm:max-w-[75%]",
                                    ehMinha
                                      ? "bg-[#DCF8C6] text-gray-800 rounded-br-md"
                                      : "border border-gray-200 bg-white text-gray-800 rounded-bl-md",
                                  ].join(" ")}
                                >
                                  <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
                                    {mensagem.texto}
                                  </div>

                                  <div
                                    className={`mt-1 text-right text-[11px] leading-none ${
                                      ehMinha
                                        ? "text-gray-500"
                                        : "text-gray-400"
                                    }`}
                                  >
                                    {formatarHoraMensagem(mensagem)}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
              </div>

              <div className="shrink-0 border-t border-gray-200 bg-white p-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        enviarMensagem();
                      }
                    }}
                    placeholder="Digite uma mensagem..."
                    className="flex-1 rounded-full border border-gray-300 px-4 py-3 text-sm outline-none focus:border-[#FE5668]"
                    disabled={!conversaSelecionada || enviando}
                    autoComplete="off"
                  />

                  <button
                    type="button"
                    onClick={enviarMensagem}
                    disabled={!conversaSelecionada || enviando}
                    className="rounded-full bg-[#FE5668] px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {enviando ? "Enviando..." : "Enviar"}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}